import React, { Suspense, lazy } from 'react';
import { useEmbeddedService } from '../hooks/useEmbeddedService';
import type { MicroFrontendLoaderProps } from '../../../types/embedding';
import { useShellI18n } from '@/features/language/useI18n';

export const MicroFrontendLoader: React.FC<MicroFrontendLoaderProps> = ({
  service,
  className,
  onLoad,
  onError,
  fallback,
}) => {
  const {
    error,
    onLoad: handleLoad,
    onError: handleError,
  } = useEmbeddedService(service);
  const { t } = useShellI18n();

  // Dynamically import the remote module
  const RemoteComponent = React.useMemo(() => {
    if (!service.module || !service.url) {
      const err = new Error('Micro-frontend configuration missing module or url');
      handleError(err);
      return null;
    }

    // In a real implementation with Module Federation, this would use the remote container
    // For now, we simulate dynamic import of a React component
    return lazy(async () => {
      try {
        // This is a placeholder for actual module federation logic
        // We might use a library like @originjs/vite-plugin-federation
        // or a custom loader
        
        // For demonstration purposes, we'll try to import from the URL directly
        // Note: This requires the remote to be built as an ES module
        const module = await import(/* @vite-ignore */ service.url);
        
        handleLoad();
        onLoad?.();
        
        return module;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to load micro-frontend');
        handleError(error);
        onError?.(error);
        throw error;
      }
    });
  }, [service.module, service.url, handleLoad, onLoad, handleError, onError]);

  if (error) {
    return (
      <div className="embed-error">
        <h3>{t('Failed to load {{name}}', { name: service.name })}</h3>
        <button
          onClick={() => window.location.reload()}
          className="btn ghost sm"
          type="button"
        >
          {t('Retry')}
        </button>
      </div>
    );
  }

  if (!RemoteComponent) {
    return null;
  }

  return (
    <div className={`embed-frame-wrap ${className || ''}`}>
      <Suspense
        fallback={
          fallback || (
            <div className="embed-loading-overlay">
              <div className="embed-spinner" />
            </div>
          )
        }
      >
        <RemoteComponent />
      </Suspense>
    </div>
  );
};
